import React, { Fragment, useState } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import propTypes from 'prop-types'
import { ChevronDownIcon, CloseRoundedIcon } from '../../../theme/icons'
import { trimColor } from '../../../utils/infoDetails'

export const AutoComplete = React.memo(({ optionClassName, options, customComponent, onChangeCustomerName, loader, placeholderText, label, selecteditem, disabled, className, customNodataComponent, onChangeTextInput, required = true }) => {
    const [selected, setSelected] = useState({})
    const [query, setQuery] = useState('')

    const filteredList = query === '' ? options : options.filter((option) =>
        option.value
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query?.toLowerCase().replace(/\s+/g, '')))

    const isSelected = Boolean('value' in selected);

    const handleOnChange = (event) => {
        setQuery(event?.target?.value?.trim())
        onChangeTextInput(event?.target?.value)
    }

    const handleClearText = () => {
        setQuery('');
        setSelected({});
        onChangeCustomerName({ _id: null })
    }

    const onSelected = (selected) => {
        setSelected(selected);
        onChangeCustomerName(selected)
    }

    React.useEffect(() => {
        if (isSelected) {
            onChangeCustomerName(selected)
        }
        // eslint-disable-next-line 
    }, [isSelected])

    React.useEffect(() => {
        if (selecteditem && '_id' in selecteditem) {
            setSelected(selecteditem);


        }
        // eslint-disable-next-line 
    }, [selecteditem])

    return (
        <div >
            {label && <label htmlFor={label} className="mb-1.5 Z-10 block text-sm font-semibold text-secondary-400">{label}{required && <span className="text-error-400">*</span>}</label>}
            <Combobox open={false} value={selected} disabled={disabled} onChange={(selected) => onSelected(selected)} className={className}>
                {({ open }) => (
                    <div className={`relative ${customComponent ? "flex" : ""}`}>
                        <div className={`relative w-full cursor-default overflow-hidden text-left `} >
                            <Combobox.Button className={" w-full opacity-0 absolute flex items-center pr-2"}  >
                                hidden button
                            </Combobox.Button>
                            <Combobox.Input
                                className="z-10 block w-full rounded-md border text-sm border-secondary-200 text-secondary-400  placeholder:text-secondary-500 outline-none px-2 py-2 transition-all  focus:border-secondary-200 focus:ring-0 focus:ring-secondary-400"
                                placeholder={placeholderText}
                                displayValue={(person) => loader ? "Loading..." : person.value}
                                disabled={disabled || loader}
                                autoComplete="off"
                                value={selected?.value}
                                onChange={(event) => handleOnChange(event)} />

                            {!isSelected ?
                                <Combobox.Button className={"absolute inset-y-0 right-0 flex items-center pr-2"} id="open-button">
                                    <ChevronDownIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true" />
                                </Combobox.Button>
                                : <Combobox.Button disabled={disabled} onClick={handleClearText} className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    <CloseRoundedIcon className={'bg-white p-0.5'} />
                                </Combobox.Button>}
                        </div>
                        {customComponent && <>{customComponent}</>}
                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setQuery('')}>
                            <Combobox.Options static className={`${!!filteredList?.length ? "" : optionClassName} z-9999 focus:outline-none absolute mt-10 max-h-40 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm`}>
                                {filteredList.length === 0 ? (
                                    <>
                                        {customNodataComponent(query)}
                                    </>
                                ) : (
                                    filteredList?.map((option, i) => (
                                        <Combobox.Option
                                            key={option?.value + i}
                                            className={({ active }) =>
                                                ` z-9999 relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'text-gray-700 bg-secondary-100' : 'text-gray-900'}`
                                            } value={option}>
                                            {({ selected, active }) => (
                                                <div className='flex gap-2 items-center'>
                                                    {option?.color_code &&
                                                        <span style={{ borderColor: trimColor(option?.borderColor) }} className={`h-[18px] min-w-[18px] w-[18px] p-[3px] flex justify-center items-center rounded-full border-2`}>
                                                            <span style={{ backgroundColor: trimColor(option?.color_code) }} className={`inline-block m-auto rounded-full h-full w-full `} />
                                                        </span>}
                                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                        {option.value}
                                                    </span>
                                                    {selected ? (
                                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-secondary-400' : 'text-teal-600'}`}>
                                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                        </span>
                                                    ) : null}
                                                </div>
                                            )}
                                        </Combobox.Option>
                                    ))
                                )}
                            </Combobox.Options>
                        </Transition>
                    </div>)}
            </Combobox>
        </div>
    )
})

AutoComplete.propTypes = {
    options: propTypes.array,
    onChangeCustomerName: propTypes.func,
    handleTextChange: propTypes.func,
    loader: propTypes.bool,
    placeholderText: propTypes.string,
    selecteditem: propTypes.object,
    disabled: propTypes.bool,
    disabledText: propTypes.string,
    className: propTypes.string,
    hideOptions: propTypes.bool,
    onChangeTextInput: propTypes.func,
    customComponent: propTypes.element,
    customNodataComponent: propTypes.func,
    optionClassName: propTypes.string,
}

AutoComplete.defaultProps = {
    options: [],
    onChangeCustomerName: () => { },
    handleTextChange: () => { },
    loader: false,
    placeholderText: "Search...",
    selecteditem: {},
    disabled: false,
    disabledText: "Select is disbaled",
    className: '',
    hideOptions: false,
    onChangeTextInput: () => { },
    customComponent: <></>,
    customNodataComponent: () => <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
        No Data found.
    </div>,
    optionClassName: ""
}